import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import './styles.css';
import './tabFrames.css';
import { App } from './App.tsx';
import { ApiError, authStore } from './api/client.ts';
import { theme } from './theme.ts';
import { NotificationControls } from './components/NotificationControls.tsx';
import { TOAST_LIMIT } from './lib/notify.ts';
import { installSelectOnFocus } from './lib/selectOnFocus.ts';

// числовые поля: ввод заменяет старое значение (см. lib/selectOnFocus.ts)
installSelectOnFocus();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // данные свежие, пока их не изменило событие сокета
      staleTime: 60_000,
      refetchOnWindowFocus: true,
      retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
    },
  },
});

// Session changes cancel active queries and remove data before the next account renders.
let cacheSession = authStore.version();
authStore.subscribe(() => {
  if (cacheSession === authStore.version()) return;
  cacheSession = authStore.version();
  queryClient.clear();
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <MantineProvider theme={theme} defaultColorScheme="light">
      <Notifications position="top-right" limit={TOAST_LIMIT} />
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <NotificationControls />
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </MantineProvider>
  </StrictMode>,
);

// Программа открывается без интернета: файлы страницы хранятся на устройстве (см. public/sw.js)
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => { void navigator.serviceWorker.register('/sw.js').catch(() => {}); });
}
